import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Chip,
  Tooltip
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  TableChart as ExcelIcon,
  TextFields as CsvIcon,
  Settings as SettingsIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';

import ExportDialog from './ExportDialog';
import exportService from '../../services/exportService';
import useModernAlert from '../../hooks/useModernAlert';
import { ExportPreset, InvoiceFilters } from '../../types/export';

interface ExportButtonProps {
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  currentFilters?: InvoiceFilters;
  selectedInvoiceIds?: number[];
  onExportComplete?: () => void;
  showQuickExport?: boolean;
  showPresetMenu?: boolean;
  className?: string;
  sx?: any;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  variant = 'outlined',
  size = 'medium',
  currentFilters = {},
  selectedInvoiceIds = [],
  onExportComplete,
  showQuickExport = true,
  showPresetMenu = true,
  className,
  sx
}) => {
  const { showError, showSuccess, AlertComponent } = useModernAlert();
  
  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [presets, setPresets] = useState<ExportPreset[]>([]);
  const [loading, setLoading] = useState(false);

  // Menu open state
  const isMenuOpen = Boolean(anchorEl);

  // Load presets when menu opens
  const handleMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    
    if (showPresetMenu && presets.length === 0) {
      try {
        const response = await exportService.getExportPresets();
        setPresets(response.presets);
      } catch (error) {
        console.error('Failed to load presets:', error);
      }
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Quick export using default preset
  const handleQuickExport = async () => {
    try {
      setLoading(true);
      handleMenuClose();
      
      await exportService.quickExport(currentFilters);
      showSuccess('Export byl úspěšně dokončen');
      onExportComplete?.();
    } catch (error) {
      if (error instanceof Error && error.message.includes('No default export preset')) {
        // No default preset, open dialog instead
        setExportDialogOpen(true);
      } else {
        showError('Export se nezdařil');
      }
    } finally {
      setLoading(false);
    }
  };

  // Export using specific preset
  const handlePresetExport = async (preset: ExportPreset) => {
    try {
      setLoading(true);
      handleMenuClose();
      
      const exportRequest = {
        filters: { ...currentFilters, ...preset.filters },
        selectedFields: preset.selectedFields,
        exportFormat: preset.exportFormat,
        fileName: preset.name,
        presetId: preset.id
      };

      if (selectedInvoiceIds.length > 0) {
        await exportService.exportSelectedInvoices(selectedInvoiceIds, exportRequest);
      } else {
        await exportService.exportInvoices(exportRequest);
      }

      showSuccess('Export byl úspěšně dokončen');
      onExportComplete?.();
    } catch (error) {
      showError('Export se nezdařil');
    } finally {
      setLoading(false);
    }
  };

  // Open export dialog
  const handleOpenDialog = () => {
    handleMenuClose();
    setExportDialogOpen(true);
  };

  // Get button text based on selection
  const getButtonText = () => {
    if (selectedInvoiceIds.length > 0) {
      return `Export (${selectedInvoiceIds.length})`;
    }
    return 'Export';
  };

  // Get default preset
  const defaultPreset = presets.find(p => p.isDefault);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<FileDownloadIcon />}
        onClick={handleMenuOpen}
        disabled={loading}
        className={className}
        sx={sx}
      >
        {getButtonText()}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Quick export option */}
        {showQuickExport && defaultPreset && (
          <MenuItem onClick={handleQuickExport}>
            <ListItemIcon>
              <BookmarkIcon color="primary" />
            </ListItemIcon>
            <ListItemText>
              <Box>
                <Typography variant="body2">Rychlý export</Typography>
                <Typography variant="caption" color="text.secondary">
                  {defaultPreset.name} ({defaultPreset.selectedFields.length} polí)
                </Typography>
              </Box>
            </ListItemText>
          </MenuItem>
        )}

        {/* Preset menu */}
        {showPresetMenu && presets.length > 0 && (
          <>
            {showQuickExport && defaultPreset && <Divider />}
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                Presets
              </Typography>
            </MenuItem>
            {presets.map((preset) => (
              <MenuItem
                key={preset.id}
                onClick={() => handlePresetExport(preset)}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon>
                    {preset.exportFormat === 'excel' ? <ExcelIcon /> : <CsvIcon />}
                  </ListItemIcon>
                  <ListItemText>
                    <Box>
                      <Typography variant="body2">
                        {preset.name}
                        {preset.isDefault && (
                          <Chip
                            label="Výchozí"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {preset.selectedFields.length} polí • {preset.exportFormat.toUpperCase()}
                      </Typography>
                    </Box>
                  </ListItemText>
                </Box>
              </MenuItem>
            ))}
            <Divider />
          </>
        )}

        {/* Custom export option */}
        <MenuItem onClick={handleOpenDialog}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Vlastní export</Typography>
            <Typography variant="caption" color="text.secondary">
              Konfigurace polí a filtrů
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        currentFilters={currentFilters}
        selectedInvoiceIds={selectedInvoiceIds}
        onExportComplete={onExportComplete}
      />

      {AlertComponent}
    </>
  );
};

export default ExportButton;
